import { useAtom, useAtomValue } from "lib/global-context";
import {
  AnswerData,
  finalizeQuestionnaire,
  submitQuestionnaireAnswer,
} from "lib/api";
import {
  currentStepAtom,
  quizCurrentStepAtom,
  quizAnswersAtom,
  userIdAtom,
} from "lib/atoms";
import { Answer } from "types/quiz";
import {
  formatAnswers,
  GTMProps,
  trackSubmission,
} from "components/common/GTMProvider";

const useQuiz = <T extends { questionId: string; maxNumOfAnswer: number }>(
  quizSteps: T[],
  gtmProps: GTMProps,
) => {
  const [formStep, setFormStep] = useAtom(currentStepAtom);
  const [quizCurrentStep, setQuizCurrentStep] = useAtom(quizCurrentStepAtom);
  const [answers, setAnswers] = useAtom(quizAnswersAtom);

  const userId = useAtomValue(userIdAtom);

  const quizCurrentStepKey = quizSteps[quizCurrentStep - 1].questionId;

  const handleOptionSelect = (option: Answer, isRequired: boolean) => {
    setAnswers((prev) => {
      const maxNumOfAnswer = quizSteps[quizCurrentStep - 1].maxNumOfAnswer;

      // Single answer
      if (maxNumOfAnswer === 1) {
        if (
          !isRequired &&
          prev[quizCurrentStepKey]?.answers?.includes(option)
        ) {
          // Remove it if it's already there, since it's not required
          return {
            ...prev,
            [quizCurrentStepKey]: { maxNumOfAnswer, answers: [] },
          };
        }

        return {
          ...prev,
          [quizCurrentStepKey]: { maxNumOfAnswer, answers: [option] },
        };
      }

      // Multiple answers
      const currentAnswers = prev[quizCurrentStepKey]?.answers || [];

      if (currentAnswers.some((answer) => answer.number === option.number))
        return {
          ...prev,
          [quizCurrentStepKey]: {
            maxNumOfAnswer,
            answers: currentAnswers.filter(
              (answer) => answer.number !== option.number,
            ),
          },
        };

      // Remove the first answer if the number of answers exceeds the maximum
      if (currentAnswers.length >= maxNumOfAnswer) currentAnswers.shift();

      return {
        ...prev,
        [quizCurrentStepKey]: {
          maxNumOfAnswer,
          answers: [...currentAnswers, option],
        },
      };
    });
  };

  const handleNext = () => {
    const answerData: AnswerData = {
      questionId: quizCurrentStepKey,
      value: answers[quizCurrentStepKey]?.answers.map((ans) => ans.value) || [],
    };
    const promise = submitQuestionnaireAnswer(userId, answerData, gtmProps);
    if (quizCurrentStep < quizSteps.length) {
      // GTM: Track form submission
      trackSubmission({
        formId: `gcb_${userId}`,
        ...gtmProps,
        label: "next-step",
        formStep: formStep,
        quizStep: quizCurrentStep,
        answers: formatAnswers([answerData]),
      });

      setQuizCurrentStep((prev) => prev + 1);
    } else {
      // Moving to Results page, so finalize the questionnaire
      promise.then(() => finalizeQuestionnaire(userId, gtmProps));

      // GTM: Track quiz completion
      trackSubmission({
        formId: `gcb_${userId}`,
        ...gtmProps,
        label: "complete",
        formStep: formStep,
        quizStep: quizCurrentStep,
        answers: formatAnswers([answerData]),
      });

      setFormStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (quizCurrentStep > 1) {
      // GTM: Track back step
      trackSubmission({
        formId: `gcb_${userId}`,
        ...gtmProps,
        label: "back-step",
        formStep: formStep,
        quizStep: quizCurrentStep,
        answers: {},
      });

      setQuizCurrentStep((prev) => prev - 1);
    }
  };

  const currentStepData = quizSteps[quizCurrentStep - 1];
  const currentAnswer = answers[quizCurrentStepKey];
  const isLastStep = quizCurrentStep === quizSteps.length;

  return {
    formStep,
    quizCurrentStep,
    answers,
    currentStepData,
    currentAnswer,
    isLastStep,
    handleOptionSelect,
    handleNext,
    handleBack,
  };
};

export default useQuiz;
